import type { ReferenceDeletedRestoreAvailableConflict } from "@/utils/reference/referenceCreateConflict";
import {
    ReferenceCreateConflictError,
    isReferenceDeletedRestoreAvailableConflict,
} from "@/utils/reference/referenceCreateConflict";
import type { RestoreOnCreateConflictConfig } from "../useRestoreOnCreateConflict";

type PendingRestoreForm = { description: string; isActive: boolean };

export function createReferenceRestoreConfig<TDetail extends { id: string }>(options: {
    deletedRestoreAvailableCode: string;
    entityLabel: string;
    formatSuccessLabel: (code: string | undefined) => string;
    restoreDeletedWithCreateValues: (payload: {
        id: string;
        description: string;
        isActive?: boolean;
    }) => Promise<TDetail>;
    onRestored: (detail: TDetail, code: string) => void;
}): RestoreOnCreateConflictConfig<
    ReferenceDeletedRestoreAvailableConflict,
    PendingRestoreForm,
    { id: string; description: string; isActive?: boolean },
    TDetail
> {
    const {
        deletedRestoreAvailableCode,
        entityLabel,
        formatSuccessLabel,
        restoreDeletedWithCreateValues,
        onRestored,
    } = options;

    return {
        isConflictError: (err: unknown): err is ReferenceCreateConflictError =>
            err instanceof ReferenceCreateConflictError,
        isRestoreAvailable: (conflict: unknown): conflict is ReferenceDeletedRestoreAvailableConflict =>
            isReferenceDeletedRestoreAvailableConflict(conflict, deletedRestoreAvailableCode),
        modal: {
            title: `Restore removed ${entityLabel}?`,
            body: (conflict) =>
                `A removed ${entityLabel} already exists with code "${conflict.existingCode}". Restore it with the description and Active choice you entered?`,
        },
        buildRestorePayload: (conflict, pending) => ({
            id: conflict.existingId,
            description: pending.description,
            isActive: pending.isActive,
        }),
        formatSuccessMessage: (conflict) => `${formatSuccessLabel(conflict.existingCode)} restored successfully!`,
        restoreDeletedWithCreateValues,
        onRestored: (detail, conflict) => onRestored(detail, conflict.existingCode),
    };
}

export type { PendingRestoreForm as ReferencePendingRestoreForm };
